/**
 * CheckTrackerCard — reusable payment lifecycle visualization.
 *
 * Renders the state machine for a deal_payments row. The path (8-state vs 3-state)
 * is driven by iink_required, NOT method/kind — see spec §10e. All state-advance
 * buttons are always visible (greyed when not yet applicable, per Greg's rule).
 *
 * Stuck >10d in any iink in-flight state: red border + pulsing chip + "Chase iink"
 * action. Threshold tunable via finance_settings.iink_stuck_alert_days.
 *
 * Spec: memory/features/finance-board-redesign.md (Ship 3, §10c #1, §10e)
 *
 * Usage:
 *   <CheckTrackerCard payment={p} compact onAdvanced={reload} />
 */

import { useState } from "react";
import { supabase } from "../../lib/supabase";
import type { DealPayment, PaymentState } from "../../lib/types";
import { fmtUSD } from "../../lib/analytics";
import DealLink from "../DealLink";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import { useAuth } from "../../context/AuthContext";
import { daysLeftLabel, daysUntil, depositShouldBeThere, fmtVerifyDay, fmtVerifyDeadline } from "../../lib/bankVerification";

// Step definitions per path. The state machine collapses to PERSONAL_STEPS
// when iink_required is false (HO personal check OR insurance check direct-to-contractor).
const INSURANCE_STEPS: Array<{ key: PaymentState; label: string; short: string }> = [
  { key: "collected",         label: "📷 Collected",      short: "Coll"   },
  { key: "iink_submitted",    label: "✉ iink Submitted",  short: "iink↑"  },
  { key: "mortgage_endorsed", label: "🏛 Mortgage End.",  short: "Mort✓"  },
  { key: "fedex_out",         label: "✈ FedEx → Mortgagor", short: "FdX→" },
  { key: "fedex_back",        label: "📦 FedEx ← Returned", short: "←FdX" },
  { key: "iink_cleared",      label: "✓ iink Cleared",    short: "iink✓" },
  { key: "deposited",         label: "🏦 Deposited",      short: "Dep"   },
  { key: "bank_cleared",      label: "✅ Bank Cleared",   short: "✓✓"    },
];

const PERSONAL_STEPS: Array<{ key: PaymentState; label: string }> = [
  { key: "collected",    label: "📷 Collected" },
  { key: "deposited",    label: "🏦 Deposited" },
  { key: "bank_cleared", label: "✅ Bank Cleared" },
];

const STRIPE_STEPS: Array<{ key: PaymentState; label: string }> = [
  { key: "pending",      label: "🕓 Pending" },
  { key: "made",         label: "💳 Made" },
  { key: "cleared",      label: "✅ Cleared" },
];

// Direct-cleared steps for ACH/wire/cash entered already-cleared via the manual
// Record-payment modal (May 7, 2026). These methods don't walk the iink/deposit
// lifecycle — the money was already in the bank when Shannon entered it.
const DIRECT_STEPS: Array<{ key: PaymentState; label: string }> = [
  { key: "cleared", label: "✅ Cleared" },
];

const STUCK_DAYS_THRESHOLD = 10;  // matches finance_settings.iink_stuck_alert_days default

function daysSince(ts: string | null): number {
  if (!ts) return 0;
  return Math.floor((Date.now() - new Date(ts).getTime()) / 86_400_000);
}

function timestampForState(p: DealPayment, state: PaymentState): string | null {
  switch (state) {
    case "collected":         return p.collected_at;
    case "iink_submitted":    return p.iink_submitted_at;
    case "mortgage_endorsed": return p.mortgage_endorsed_at;
    case "fedex_out":         return p.fedex_out_at;
    case "fedex_back":        return p.fedex_back_at;
    case "iink_cleared":      return p.iink_cleared_at;
    case "deposited":         return p.deposited_at;
    case "bank_cleared":      return p.bank_cleared_at;
    case "made":              return p.stripe_made_at;
    case "cleared":           return p.stripe_cleared_at;
    case "disputed":          return p.stripe_disputed_at;
    case "refunded":          return p.stripe_refunded_at;
    default:                  return null;
  }
}

// What "advance" means in the iink path: which state comes next + which timestamp to set.
const IINK_ADVANCE: Record<string, { next: PaymentState; tsCol: string; label: string }> = {
  collected:         { next: "iink_submitted",    tsCol: "iink_submitted_at",    label: "✉ Submit to iink" },
  iink_submitted:    { next: "mortgage_endorsed", tsCol: "mortgage_endorsed_at", label: "🏛 Mark mortgage endorsed" },
  mortgage_endorsed: { next: "fedex_out",         tsCol: "fedex_out_at",         label: "✈ Log FedEx out" },
  fedex_out:         { next: "fedex_back",        tsCol: "fedex_back_at",        label: "📦 Mortgagor returned" },
  fedex_back:        { next: "iink_cleared",      tsCol: "iink_cleared_at",      label: "✓ Confirm iink cleared" },
  iink_cleared:      { next: "deposited",         tsCol: "deposited_at",         label: "🏦 Mark deposited" },
  deposited:         { next: "bank_cleared",      tsCol: "bank_cleared_at",      label: "✅ Mark bank cleared" },
};

// Personal/no-iink path: 3 states.
const PERSONAL_ADVANCE: Record<string, { next: PaymentState; tsCol: string; label: string }> = {
  collected: { next: "deposited",    tsCol: "deposited_at",    label: "🏦 Mark deposited" },
  deposited: { next: "bank_cleared", tsCol: "bank_cleared_at", label: "✅ Mark bank cleared" },
};

function toE164(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const d = phone.replace(/\D/g, "");
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d[0] === "1") return `+${d}`;
  return null;
}

function firstNameOf(name: string | null | undefined): string {
  const first = (name ?? "").trim().split(/\s+/)[0] ?? "";
  if (!first) return "there";
  return first === first.toUpperCase() ? first.charAt(0) + first.slice(1).toLowerCase() : first;
}

export default function CheckTrackerCard({
  payment,
  compact = false,
  onAdvanced,
  onIinkToggled,
  // Optional deal context — passed from ShannonPaymentsTab after the deal join
  dealId,
  homeownerName,
  homeownerPhone,
  street,
  amountOwed,
  // Privileged edit/delete (May 20, 2026). When canManage is true, an Edit +
  // Delete control renders. onEdit opens the parent's PaymentEditModal.
  canManage = false,
  onEdit,
  onDeleted,
  // Who the bank-verification text goes to, when the card has no homeowner
  // header (the deal page's Payments tab passes these; the Finance board
  // passes homeownerName / homeownerPhone, which are used instead).
  textToName,
  textToPhone,
}: {
  payment: DealPayment;
  compact?: boolean;
  onAdvanced?: (p: DealPayment) => void;
  onIinkToggled?: (newValue: boolean) => void;
  dealId?: string;
  homeownerName?: string | null;
  homeownerPhone?: string | null;
  street?: string | null;
  amountOwed?: number;
  canManage?: boolean;
  onEdit?: () => void;
  onDeleted?: () => void;
  textToName?: string | null;
  textToPhone?: string | null;
}) {
  const { member } = useAuth();
  const [acting, setActing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [textBusy, setTextBusy] = useState(false);
  const [textMsg, setTextMsg] = useState<string | null>(null);
  // Confirmation modal — true when the user has clicked the button but not yet confirmed
  const [confirmText, setConfirmText] = useState(false);
  // Same, for texting the bank-verification link
  const [confirmVerifyText, setConfirmVerifyText] = useState(false);
  const [copyMsg, setCopyMsg] = useState<string | null>(null);
  // Delete confirm — inline two-step so an accidental tap can't drop a payment.
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function deletePayment() {
    setActing(true);
    setError(null);
    try {
      const { error: delErr } = await supabase.from("deal_payments").delete().eq("id", payment.id);
      if (delErr) throw delErr;
      setConfirmDelete(false);
      onDeleted?.();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setActing(false);
    }
  }

  const isStripe = payment.method === "stripe";
  const isCheck = payment.method === "check" || payment.method === "ach" || payment.method === "wire";

  // Stripe ACH typed in by hand, waiting on the homeowner to confirm the test
  // deposit. The row stays at "made" so the pay buttons never ask for the
  // money twice; ach_verify_status is what says the homeowner still has to act.
  const verifyPending = isStripe && payment.state === "made" && payment.ach_verify_status === "pending";
  const verifyEnded = isStripe && (
    payment.ach_verify_status === "expired" ||
    payment.ach_verify_status === "failed" ||
    payment.ach_verify_status === "canceled"
  );
  const verifyDone = isStripe && payment.ach_verify_status === "verified";
  const verifyArrival = fmtVerifyDay(payment.ach_microdeposit_arrival_at);
  const verifyArrived = depositShouldBeThere(payment.ach_microdeposit_arrival_at);
  const verifyDeadline = fmtVerifyDeadline(payment.ach_verify_deadline_at);
  const verifyDaysLeft = daysUntil(payment.ach_verify_deadline_at);
  const verifyName = textToName ?? homeownerName;
  const verifyPhone = textToPhone ?? homeownerPhone;
  const depositHint = payment.ach_microdeposit_type === "amounts"
    ? "two small deposits (under $1 each)"
    : "a $0.01 deposit whose description has a 6-character code starting with SM";
  // Manual non-check entries (ach/wire/cash) created already-cleared by the
  // Record-payment modal — show a single ✅ Cleared pill instead of an empty
  // iink/personal strip with nothing highlighted.
  const isDirectCleared =
    (payment.method === "ach" || payment.method === "wire" || payment.method === "cash") &&
    payment.state === "cleared";

  const steps = isStripe
    ? STRIPE_STEPS
    : isDirectCleared
    ? DIRECT_STEPS
    : payment.iink_required
    ? INSURANCE_STEPS
    : PERSONAL_STEPS;

  const currentIdx = steps.findIndex((s) => s.key === payment.state);
  const currentStateTs = timestampForState(payment, payment.state);
  const daysInState = daysSince(currentStateTs);
  const isStuck =
    payment.iink_required &&
    isCheck &&
    payment.state !== "bank_cleared" &&
    payment.state !== "deposited" &&
    daysInState >= STUCK_DAYS_THRESHOLD;

  // What advance action applies right now?
  const advanceMap = payment.iink_required ? IINK_ADVANCE : PERSONAL_ADVANCE;
  const advance = isCheck ? advanceMap[payment.state as string] : null;

  async function advanceState() {
    if (!advance) return;
    setActing(true);
    setError(null);
    try {
      const update: Record<string, unknown> = {
        state: advance.next,
        [advance.tsCol]: new Date().toISOString(),
      };
      const { data, error: updateErr } = await supabase
        .from("deal_payments")
        .update(update)
        .eq("id", payment.id)
        .select("*")
        .single();
      if (updateErr) throw updateErr;
      onAdvanced?.(data as DealPayment);
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setActing(false);
    }
  }

  async function toggleIinkRequired() {
    setActing(true);
    setError(null);
    try {
      const newValue = !payment.iink_required;
      const { error: updateErr } = await supabase
        .from("deal_payments")
        .update({ iink_required: newValue })
        .eq("id", payment.id);
      if (updateErr) throw updateErr;
      onIinkToggled?.(newValue);
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setActing(false);
    }
  }

  // Body builder — called both to preview in the confirm modal and to send.
  function buildPaymentBody(): string {
    const firstName = homeownerName?.split(" ")[0] ?? "there";
    const loc = street ?? "your property";
    const balance = amountOwed != null ? fmtUSD(amountOwed) : "your remaining balance";
    return (
      `Hi ${firstName}, this is Shannon at Bardin Roofing. You have ${balance} ` +
      `for your roof at ${loc}. Pay online: portal.bardinroofing.com ` +
      `— or call (470) 823-8816.`
    );
  }

  async function sendPaymentLink(body: string) {
    const phone = toE164(homeownerPhone);
    if (!phone) { setTextMsg("No phone on file"); setTimeout(() => setTextMsg(null), 3500); return; }
    setTextBusy(true);
    setTextMsg(null);
    try {
      const { error: smsErr } = await supabase.functions.invoke("twilio-send-sms", {
        body: { to: phone, body, deal_id: dealId ?? payment.deal_id, tag: "payment_link_check" },
      });
      if (smsErr) throw smsErr;
      if (dealId ?? payment.deal_id) {
        await supabase.from("activity_log").insert({
          deal_id: dealId ?? payment.deal_id,
          activity_type: "ar_contact",
          title: "Payment link texted to homeowner",
          description: `Payment link SMS sent to ${homeownerName ?? "homeowner"} via Payments tab.`,
          metadata: { tag: "payment_link_check", payment_id: payment.id },
        });
      }
      setTextMsg("✓ Sent");
    } catch {
      setTextMsg("Failed — check number");
    } finally {
      setTextBusy(false);
      setTimeout(() => setTextMsg(null), 4000);
    }
  }

  // The bank-verification text. GSM-7 only (no em-dashes or curly quotes) so
  // it stays in 153-character segments; leads with Peach Duck Contracting.
  function buildVerifyBody(): string {
    const how = payment.ach_microdeposit_type === "amounts"
      ? "Stripe sent two small deposits (under $1 each) to your bank. Enter the two amounts here to finish: "
      : "Stripe sent a $0.01 deposit to your bank; its description has a code starting with SM. Enter that code here to finish: ";
    const by = verifyDeadline ? ` Please finish by ${verifyDeadline} or the payment is cancelled.` : "";
    return (
      `Hi ${firstNameOf(verifyName)}, it's Peach Duck Contracting. ` +
      `Your ${fmtUSD(Number(payment.amount))} bank payment needs one more step. ` +
      how + (payment.ach_verify_url ?? "") + by + " Reply here with any questions."
    );
  }

  async function sendVerifyLink(body: string) {
    const phone = toE164(verifyPhone);
    if (!phone) { setTextMsg("No phone on file"); setTimeout(() => setTextMsg(null), 3500); return; }
    setTextBusy(true);
    setTextMsg(null);
    try {
      // sent_by makes the homeowner's reply come back to whoever sent this.
      const { error: smsErr } = await supabase.functions.invoke("twilio-send-sms", {
        body: {
          to: phone,
          body,
          deal_id: dealId ?? payment.deal_id,
          tag: "ach_verify_manual",
          sent_by: member?.id ?? null,
        },
      });
      if (smsErr) throw smsErr;
      await supabase.from("activity_log").insert({
        deal_id: dealId ?? payment.deal_id,
        activity_type: "ar_contact",
        title: "Bank verification link texted to homeowner",
        description: `Texted ${verifyName ?? "the homeowner"} the link to verify their bank account for the ${fmtUSD(Number(payment.amount))} payment.`,
        performed_by: member?.id ?? null,
        metadata: { tag: "ach_verify_manual", payment_id: payment.id },
      });
      setTextMsg("✓ Sent");
    } catch {
      setTextMsg("Failed — check number");
    } finally {
      setTextBusy(false);
      setTimeout(() => setTextMsg(null), 4000);
    }
  }

  async function copyVerifyLink() {
    if (!payment.ach_verify_url) return;
    try {
      await navigator.clipboard.writeText(payment.ach_verify_url);
      setCopyMsg("✓ Copied");
    } catch {
      setCopyMsg("Couldn't copy");
    }
    setTimeout(() => setCopyMsg(null), 3000);
  }

  const stuckClass = isStuck
    ? "border-2 border-red-400 bg-red-50/40"
    : "border border-slate-200 bg-white";

  return (
    <div className={`rounded-lg p-3 ${stuckClass}`}>
      {/* Homeowner context — rendered when caller passes deal context */}
      {(homeownerName || street) && (
        <div className="mb-2 pb-2 border-b border-slate-100 flex items-baseline justify-between gap-2">
          <div className="min-w-0">
            {homeownerName && (dealId ?? payment.deal_id) ? (
              <DealLink id={dealId ?? payment.deal_id} className="text-sm font-bold text-bardin-ink hover:underline">
                {homeownerName}
              </DealLink>
            ) : homeownerName ? (
              <span className="text-sm font-bold text-bardin-ink">{homeownerName}</span>
            ) : null}
            {street && <span className="text-xs text-slate-500 ml-1.5">{street}</span>}
          </div>
          {!homeownerPhone && (
            <span className="text-[10px] text-amber-600 font-medium flex-shrink-0">⚠ no phone</span>
          )}
        </div>
      )}

      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-bardin-ink">
              {labelForMethod(payment.method)}{payment.kind === "personal" ? " (HO personal)" : payment.kind === "insurance" ? " (Insurance)" : ""}
            </span>
            {isCheck && <IinkChip required={payment.iink_required} onToggle={toggleIinkRequired} disabled={acting} />}
            {payment.state === "disputed" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">🆘 DISPUTED</span>}
            {payment.state === "refunded" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">REFUNDED</span>}
            {verifyPending && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">🏦 Needs bank verification</span>}
            {verifyEnded && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">🏦 Never verified</span>}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {labelForSlot(payment.slot)} · {fmtUSD(payment.amount)}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-sm font-semibold text-bardin-ink">{fmtUSD(payment.amount, { short: true })}</div>
          <div className="text-[10px] text-slate-400">{payment.state.replace(/_/g, " ")}</div>
        </div>
      </div>

      {/* Pipeline visualization */}
      <div className="flex flex-wrap gap-1 mt-3">
        {steps.map((s, i) => {
          const isCurrent = i === currentIdx;
          const isPast = i < currentIdx;
          const isStuckHere = isCurrent && isStuck;
          const labelText = "label" in s ? s.label : "";
          const cls = isStuckHere
            ? "bg-red-100 text-red-700 animate-pulse"
            : isCurrent
            ? "bg-bardin-orange text-white"
            : isPast
            ? "bg-emerald-100 text-emerald-700"
            : "bg-slate-100 text-slate-500";
          return (
            <div key={s.key} className="flex items-center gap-1">
              <span className={`text-[10px] font-semibold px-2 py-1 rounded ${cls}`}>
                {compact && "short" in s ? (s as any).short : labelText}
                {isCurrent && daysInState > 0 ? ` · ${daysInState}d${isStuck ? " ⚠" : ""}` : ""}
              </span>
              {i < steps.length - 1 && <span className="text-slate-300 text-xs">›</span>}
            </div>
          );
        })}
      </div>

      {/* Stripe ACH typed in by hand: nothing is charged until the homeowner
          confirms Stripe's test deposit (Oct 20, 2026). */}
      {verifyPending && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 space-y-1.5">
          <div className="font-semibold">Waiting on the homeowner to verify their bank account</div>
          <div>
            They typed their bank numbers in by hand, so Stripe sent {depositHint} to that account.
            Nothing is charged until they enter it on Stripe's verify page.
          </div>
          {(verifyArrival || verifyDeadline) && (
            <div className="text-amber-800">
              {verifyArrival && (verifyArrived
                ? <>The deposit should already be in their account (Stripe expected it {verifyArrival}). </>
                : <>Deposit expected {verifyArrival}. </>)}
              {verifyDeadline && (
                <>
                  Verify by {verifyDeadline}
                  {verifyDaysLeft != null && (
                    <span className="font-semibold">
                      {" "}({daysLeftLabel(verifyDaysLeft)})
                    </span>
                  )}
                  , or Stripe cancels the payment.
                </>
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-2 items-center pt-0.5">
            {payment.ach_verify_url ? (
              <>
                <button
                  onClick={() => setConfirmVerifyText(true)}
                  disabled={textBusy || acting}
                  className="px-2.5 py-1 text-[11px] font-semibold rounded bg-amber-600 text-white hover:opacity-90 disabled:opacity-50"
                >
                  {textBusy ? "Sending…" : "📱 Text the verify link"}
                </button>
                <button
                  onClick={copyVerifyLink}
                  className="px-2.5 py-1 text-[11px] font-medium rounded bg-white border border-amber-300 text-amber-800 hover:bg-amber-100"
                >
                  📋 Copy verify link
                </button>
              </>
            ) : (
              <span className="italic text-amber-800">Stripe hasn't shared the verify link yet. It refreshes every 30 minutes.</span>
            )}
            {copyMsg && <span className="text-[11px] font-medium text-emerald-700">{copyMsg}</span>}
          </div>
        </div>
      )}

      {verifyEnded && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          The homeowner never confirmed Stripe's test deposit, so Stripe cancelled this payment
          {payment.ach_verify_ended_at ? ` on ${fmtVerifyDay(payment.ach_verify_ended_at)}` : ""}. Nothing was charged.
          If they haven't paid another way since, they need to pay again; logging in to their bank
          when they pay skips the test deposit.
        </div>
      )}

      {/* Action buttons — always visible, greyed when not applicable */}
      <div className="mt-3 flex flex-wrap gap-2 items-center">
        {isCheck && advance && (
          <button
            onClick={advanceState}
            disabled={acting}
            className="px-3 py-1.5 text-xs font-medium rounded bg-bardin-orange text-white hover:opacity-90 disabled:opacity-50"
          >
            {acting ? "Saving…" : advance.label}
          </button>
        )}
        {isStuck && (
          <button
            onClick={() => alert("📞 In production: opens your iink chase modal — pre-fills email/phone with the iink case ID.")}
            className="px-3 py-1.5 text-xs font-medium rounded bg-red-100 text-red-700 hover:bg-red-200"
          >
            📞 Chase iink
          </button>
        )}
        {isStripe && payment.state === "made" && !verifyPending && (
          <span className="text-[11px] text-slate-500 italic">
            {verifyDone
              ? "Bank account verified. Stripe is collecting the funds and clears it on its own in a few business days."
              : "Stripe will auto-clear once funds settle (~2 biz days). No action needed."}
          </span>
        )}
        {/* Text Payment Link — visible on in-flight checks (not yet deposited) when deal context
            is supplied. Excluded on deposited/cleared states — homeowner has already paid,
            we're just waiting on bank/QB confirmation at that point. */}
        {isCheck && payment.state !== "deposited" && payment.state !== "cleared" && (homeownerName || homeownerPhone) && (
          <button
            onClick={() => setConfirmText(true)}
            disabled={textBusy || acting}
            className="px-3 py-1.5 text-xs font-medium rounded bg-sky-100 text-sky-700 hover:bg-sky-200 disabled:opacity-50"
          >
            {textBusy ? "Sending…" : "📱 Text Payment Link"}
          </button>
        )}
        {textMsg && (
          <span className={"text-[11px] font-medium " + (textMsg.startsWith("✓") ? "text-emerald-600" : "text-red-500")}>
            {textMsg}
          </span>
        )}

        {/* Privileged edit/delete (May 20, 2026) — super-users + finance/exec tier */}
        {canManage && !confirmDelete && (
          <>
            <button
              onClick={onEdit}
              disabled={acting}
              className="px-3 py-1.5 text-xs font-medium rounded bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50"
            >
              ✏️ Edit
            </button>
            <button
              onClick={() => setConfirmDelete(true)}
              disabled={acting}
              className="px-3 py-1.5 text-xs font-medium rounded bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
            >
              🗑 Delete
            </button>
          </>
        )}
        {canManage && confirmDelete && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-red-700">Delete this {labelForMethod(payment.method).toLowerCase()} payment of {fmtUSD(payment.amount)}?</span>
            <button
              onClick={deletePayment}
              disabled={acting}
              className="px-3 py-1.5 text-xs font-semibold rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {acting ? "Deleting…" : "Confirm delete"}
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              disabled={acting}
              className="px-3 py-1.5 text-xs font-medium rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-2 text-xs text-red-600 bg-red-50 rounded px-2 py-1">⚠ {error}</div>
      )}

      {/* Confirm modal — shown after clicking "Text Payment Link", before the SMS fires */}
      {confirmText && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-5">
            <h3 className="text-sm font-semibold text-bardin-ink mb-0.5">📱 Text Payment Link</h3>
            <p className="text-xs text-slate-500 mb-3">
              To: <span className="font-medium text-bardin-ink">{homeownerName ?? "homeowner"}</span>
              {homeownerPhone ? <> · {homeownerPhone}</> : <span className="text-amber-600"> · ⚠ no phone on file</span>}
            </p>
            {/* Message preview — exactly what will be sent */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-xs text-slate-700 leading-relaxed mb-4">
              {buildPaymentBody()}
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmText(false)}
                className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setConfirmText(false);
                  sendPaymentLink(buildPaymentBody());
                }}
                disabled={!toE164(homeownerPhone)}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-sky-600 text-white hover:opacity-90 disabled:opacity-40"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm modal for the bank-verification text */}
      {confirmVerifyText && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-5">
            <h3 className="text-sm font-semibold text-bardin-ink mb-0.5">📱 Text the verify link</h3>
            <p className="text-xs text-slate-500 mb-3">
              To: <span className="font-medium text-bardin-ink">{verifyName ?? "homeowner"}</span>
              {verifyPhone ? <> · {verifyPhone}</> : <span className="text-amber-600"> · ⚠ no phone on file</span>}
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-xs text-slate-700 leading-relaxed mb-4 break-words">
              {buildVerifyBody()}
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmVerifyText(false)}
                className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setConfirmVerifyText(false);
                  sendVerifyLink(buildVerifyBody());
                }}
                disabled={!toE164(verifyPhone)}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-600 text-white hover:opacity-90 disabled:opacity-40"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function IinkChip({
  required,
  onToggle,
  disabled,
}: {
  required: boolean;
  onToggle: () => void;
  disabled: boolean;
}) {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      title={
        required
          ? "iink endorsement required (set by RM at check upload). Click to flip if RM was wrong."
          : "No iink endorsement (HO personal OR insurance check direct-to-contractor). Click to flip on if mortgagor named."
      }
      className={
        "text-[10px] font-bold px-1.5 py-0.5 rounded cursor-pointer disabled:cursor-not-allowed " +
        (required
          ? "bg-purple-100 text-purple-700 hover:bg-purple-200"
          : "bg-slate-100 text-slate-600 hover:bg-slate-200")
      }
    >
      {required ? "🏛 iink required" : "No iink"}
    </button>
  );
}

function labelForMethod(m: string): string {
  return ({
    check: "Check",
    stripe: "Stripe",
    ach: "ACH",
    wire: "Wire",
    cash: "Cash",
  } as Record<string, string>)[m] ?? m;
}

function labelForSlot(s: string): string {
  return ({
    pre_build: "Pre-build payment",
    post_build: "Post-build payment",
    supplement: "Supplement payment",
    other: "Other",
  } as Record<string, string>)[s] ?? s;
}
